import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const inputVariants = cva(
  "flex h-10 w-full rounded-md border bg-background text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm min-h-[44px] transition-colors duration-200",
  {
    variants: {
      variant: {
        default: "border-input px-3 py-2",
        search: "input-search",
        ghost: "border-transparent bg-transparent focus-visible:border-input",
        outline: "border-primary/50 focus-visible:border-primary",
      },
      size: {
        default: "h-10 px-3 py-2",
        sm: "h-8 px-2 py-1 text-sm",
        lg: "h-12 px-4 py-3 text-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface InputProps
  extends Omit<React.ComponentProps<"input">, "size">,
    VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, size: variantSize, type, ...props }, ref) => {
    // Enhanced mobile keyboard optimization
    const getMobileProps = () => {
      const mobileProps: any = {};
      
      switch (type) {
        case 'email':
          mobileProps.inputMode = 'email';
          mobileProps.autoComplete = 'email';
          mobileProps.autoCapitalize = 'none';
          mobileProps.autoCorrect = 'off';
          break;
        case 'tel':
          mobileProps.inputMode = 'tel';
          mobileProps.autoComplete = 'tel';
          break;
        case 'url':
          mobileProps.inputMode = 'url';
          mobileProps.autoComplete = 'url';
          mobileProps.autoCapitalize = 'none';
          mobileProps.autoCorrect = 'off';
          break;
        case 'search':
          mobileProps.inputMode = 'search';
          mobileProps.autoComplete = 'off';
          mobileProps.autoCapitalize = 'none';
          mobileProps.autoCorrect = 'off';
          break;
        case 'number':
          mobileProps.inputMode = 'numeric';
          mobileProps.pattern = '[0-9]*';
          break;
        case 'text':
        default:
          if (props.name?.includes('name')) {
            mobileProps.autoComplete = 'name';
            mobileProps.autoCapitalize = 'words';
          } else if (props.name?.includes('address')) {
            mobileProps.autoComplete = 'address-line1';
            mobileProps.autoCapitalize = 'words';
          }
          break;
      }
      
      return mobileProps;
    };

    return (
      <input
        type={type}
        className={cn(inputVariants({ variant, size: variantSize, className }))}
        ref={ref}
        {...getMobileProps()}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input, inputVariants }
